import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Wrench, Headset, Search, PenTool, Settings2, GraduationCap, ArrowRight, Download, Sparkles, BadgeCheck } from 'lucide-react';
import { BrandIcon } from '../components/BrandIcon';
import { Testimonials } from '../components/Testimonials';
import sprintContent from '../data/sprintContent.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pillars = [
  {
    icon: Briefcase,
    title: 'Fractional COO & Operations Advisory',
    scope: 'Retainer-based strategic operations leadership.',
    details: 'Ongoing executive guidance, workflow overhauls, and operating-cadence design for founders and leaders who need senior CX/ops horsepower without a full-time hire.',
    model: 'Quarterly Retainer'
  },
  {
    icon: Wrench,
    title: 'Support Architecture & Tech Stack Optimization',
    scope: 'A focused 4-week sprint.',
    details: 'Audit, redesign, and configure your helpdesk, ticketing tools, macros, triggers, and automation workflows so your stack scales with volume instead of buckling under it.',
    model: '4-week fixed sprint'
  },
  {
    icon: Headset,
    title: 'Voice of the Customer & De-Escalation Frameworks',
    scope: 'Custom feedback loops + frontline training.',
    details: 'Build listening posts that surface real signal, plus training programs focused on high-stakes communication, de-escalation, and retention of your most valuable accounts.',
    model: 'Program engagement'
  }
];

const weekIcons = [Search, PenTool, Settings2, GraduationCap];
const timeline = sprintContent.weeks.map((w, i) => ({ ...w, icon: weekIcons[i] || Search }));
const metrics = sprintContent.metrics;

const Services = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-[7.6923%]">
          <div className="max-w-4xl">
            <span className="inline-block text-[14px] font-semibold uppercase tracking-wider text-[#00D4B4] mb-6">
              The 90-Day Sprint
            </span>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span
                data-testid="services-cert-badge"
                className="inline-flex items-center gap-2 bg-[#E1F5EE] text-[#085041] text-[13px] font-semibold px-4 py-2"
              >
                <Sparkles className="w-4 h-4" /> Google AI Certified
              </span>
              <span
                data-testid="services-itil-badge"
                className="inline-flex items-center gap-2 bg-[#E1F5EE] text-[#085041] text-[13px] font-semibold px-4 py-2"
              >
                <BadgeCheck className="w-4 h-4" /> ITIL 4 Certified
              </span>
            </div>
            <h1 className="text-[36px] sm:text-[48px] lg:text-[66px] font-semibold leading-[1.05] tracking-[-0.02em] text-gray-900 mb-8">
              Turn Your Support Helpdesk From a Bottleneck Into a Scalable Engine in 90 Days.
            </h1>
            <p className="text-[20px] text-gray-600 leading-[1.5] mb-10">
              Practical, hands-on consulting to redesign how your support team operates — from the tech stack up to executive strategy.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="services-hero-cta"
                className="inline-flex items-center gap-3 bg-[#00D4B4] text-white px-8 py-5 text-[18px] font-medium hover:bg-[#00BFA3] transition-all duration-400"
                style={{ borderRadius: '0px' }}
              >
                Book Your Sprint Audit <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`${API}/sprint-onepager.pdf`}
                data-testid="services-download-onepager"
                className="inline-flex items-center gap-3 bg-white text-gray-900 border-2 border-gray-300 px-8 py-5 text-[18px] font-medium hover:border-[#00D4B4] hover:text-[#00D4B4] transition-all duration-400"
                style={{ borderRadius: '0px' }}
              >
                <Download className="w-5 h-5" /> Download the One-Pager
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics band */}
      <section className="py-16 bg-[#085041]">
        <div className="container mx-auto px-[7.6923%]">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((m, i) => (
              <div key={i} className="text-center" data-testid={`services-metric-${i}`}>
                <div className="text-[36px] md:text-[48px] font-semibold text-[#00D4B4] leading-none mb-2">{m.value}</div>
                <div className="text-[15px] text-[#E1F5EE]/90">{m.label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-[#E1F5EE]/60 mt-8 max-w-3xl mx-auto">
            {sprintContent.disclaimer}
          </p>
        </div>
      </section>

      {/* Core pillars */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-[7.6923%]">
          <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-gray-900 mb-4">
            Three Ways I Help
          </h2>
          <p className="text-[18px] text-gray-600 mb-12">Clear scopes, clear delivery models — no open-ended retainers unless you want one.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((p, i) => (
              <div key={i} data-testid={`services-pillar-${i}`} className="bg-gray-50 p-8 border border-gray-200 hover:border-[#00D4B4] hover:shadow-lg transition-all duration-400 group flex flex-col">
                <BrandIcon icon={p.icon} className="mb-6 group-hover:scale-110 transition-transform duration-400" />
                <h3 className="text-[24px] font-semibold text-gray-900 mb-2">{p.title}</h3>
                <div className="text-[15px] font-medium text-[#00D4B4] mb-4">{p.scope}</div>
                <p className="text-[17px] text-gray-600 leading-[1.6] flex-1">{p.details}</p>
                <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-[14px] uppercase tracking-wide text-gray-500">{p.model}</span>
                  <Link to="/contact" data-testid={`services-pillar-cta-${i}`} className="inline-flex items-center gap-2 text-[15px] font-medium text-[#00D4B4] hover:gap-3 transition-all duration-300">
                    Inquire <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4-week timeline */}
      <section className="py-20 md:py-32 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-[7.6923%]">
          <div className="max-w-3xl mb-16">
            <span className="inline-block text-[14px] font-semibold uppercase tracking-wider text-[#00D4B4] mb-4">
              The 4-Week Sprint
            </span>
            <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-gray-900">
              How the Support Sprint Works
            </h2>
            <p className="text-[18px] text-gray-600 leading-[1.6] mt-4">
              Every sprint is grounded in ITIL 4 service-management best practices — so the workflows, SLAs, and processes we build follow a proven, globally recognized standard, not guesswork.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((t, i) => (
              <div key={i} data-testid={`sprint-step-${i}`} className="relative bg-white p-8 border border-gray-200">
                <div className="text-[56px] font-bold text-gray-100 leading-none absolute top-4 right-5 select-none">{i + 1}</div>
                <BrandIcon icon={t.icon} className="mb-6" />
                <div className="text-[14px] font-semibold uppercase tracking-wide text-[#00D4B4] mb-2">{t.week}</div>
                <h3 className="text-[22px] font-semibold text-gray-900 mb-3">{t.title}</h3>
                <p className="text-[16px] text-gray-600 leading-[1.6]">{t.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-24 -mb-8">
            <Testimonials />
          </div>

          <div className="text-center mt-20">
            <div className="inline-block bg-gradient-to-br from-[#00D4B4] to-[#00BFA3] p-6 sm:p-12 max-w-3xl shadow-xl">
              <h3 className="text-[28px] md:text-[32px] font-semibold text-white mb-4">Ready to unblock your support team?</h3>
              <p className="text-[18px] text-white/95 mb-8">Book a diagnostic audit and get a clear, actionable plan for your first 90 days.</p>
              <Link
                to="/contact"
                data-testid="services-footer-cta"
                className="inline-flex items-center gap-3 bg-white text-[#00D4B4] px-8 py-5 text-[18px] font-medium hover:bg-gray-100 transition-all duration-400"
                style={{ borderRadius: '0px' }}
              >
                Book Your Sprint Audit <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
